import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";
import { useState, useMemo, useEffect, useRef, startTransition } from "react";
import ProductCard from "@/components/ProductCard";
import { HotelFiltersSidebar } from "@/components/HotelFiltersSidebar";
import { HotelSearchForm } from "@/components/HotelSearchForm";
import { LazyHotelsMapModal } from "@/components/LazyHotelsMapModal";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { HotelSearchParams, IterpecSearchResponse, IterpecHotel } from "@/types/iterpec";
import { useCart } from "@/hooks/useCart";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams, useNavigate } from "@/lib/react-router-dom-shim";
import { Button } from "@/components/ui/button";
import { Map, Search, Building2 } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { translateBoardType } from "@/utils/boardTypeTranslations";
import { SchemaOrg, getHotelListSchema, getBreadcrumbSchema, getOrganizationSchema, getWebSiteSchema } from "@/components/seo/SchemaOrg";
import { HreflangTags } from "@/components/seo/HreflangTags";
import { useTranslation } from "react-i18next";
import { usePrice } from "@/hooks/usePrice";
import { useLocale } from "@/contexts/LocaleContext";
import { LocaleKey } from "@/i18n";
import { getLocalizedUrl } from "@/hooks/useSeoMeta";
import { useRecommendedHotels } from "@/hooks/useRecommendedHotels";

export interface HotelsInitialData {
  hotels: IterpecHotel[];
  token: string | null;
  params: HotelSearchParams;
}

interface HotelsProps {
  initialData?: HotelsInitialData;
}

const Hotels = ({ initialData }: HotelsProps = {}) => {
  const { t, i18n } = useTranslation();
  const { locale } = useLocale();
  const { format: formatPrice, currency } = usePrice();
  const [searchResults, setSearchResults] = useState<IterpecHotel[]>(
    initialData?.hotels ?? []
  );
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState<boolean>(!!initialData);
  const [searchParams, setSearchParams] = useState<HotelSearchParams | null>(
    initialData?.params ?? null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [isMapOpen, setIsMapOpen] = useState(false);
  const { toast } = useToast();
  const [searchTimestamp, setSearchTimestamp] = useState<Date | null>(
    initialData ? new Date() : null
  );
  const [urlSearchParams] = useSearchParams();
  const navigate = useNavigate();
  // Skip client auto-search when the loader already hydrated initial results.
  const hasAutoSearched = useRef<boolean>(!!initialData);
  const [searchToken, setSearchToken] = useState<string | null>(
    initialData?.token ?? null
  );
  const { addItem } = useCart();
  const resultsRef = useRef<HTMLDivElement>(null);
  const [isPageChanging, setIsPageChanging] = useState(false);
  const [destinationDisplayName, setDestinationDisplayName] = useState<string>(
    initialData?.params.destinationName ?? ""
  );
  
  const ITEMS_PER_PAGE = 12;
  
  const maxPricePerNight = useMemo(() => {
    if (!searchResults || searchResults.length === 0 || !searchParams) return 500;
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    
    const pricesPerNight = searchResults.flatMap(h => 
      h.Rooms.map(r => {
        const priceEur = r.PriceEur || 0;
        return totalNights > 0 ? priceEur / totalNights : priceEur;
      }).filter((p): p is number => p > 0)
    );
    
    return pricesPerNight.length > 0 ? Math.ceil(Math.max(...pricesPerNight)) : 500;
  }, [searchResults, searchParams]);

  const boardTypes = useMemo(() => {
    if (!searchResults) return [];
    const boards = searchResults.flatMap(
      (h) => h.Rooms?.map((r) => r.BoardDescription).filter(Boolean) || []
    );
    const translatedBoards = boards.map((board) => translateBoardType(board, locale));
    return [...new Set(translatedBoards)].sort();
  }, [searchResults, locale]);

  const starRatings = useMemo(() => {
    if (!searchResults) return [];
    const ratings = searchResults.map(h => (h as any).Category || h.StarRating).filter((r): r is number => r > 0);
    return [...new Set(ratings)].sort((a, b) => b - a);
  }, [searchResults]);

  // Calculate destination center coordinates from hotel results
  const destinationCoordinates = useMemo(() => {
    if (!searchResults || searchResults.length === 0) return null;
    
    const hotelsWithCoords = searchResults.filter(h => {
      const lat = h.Position?.Latitude || h.Latitude;
      const lng = h.Position?.Longitude || h.Longitude;
      return lat && lng;
    });
    
    if (hotelsWithCoords.length === 0) return null;
    
    const totalLat = hotelsWithCoords.reduce((sum, h) => {
      return sum + (h.Position?.Latitude || h.Latitude || 0);
    }, 0);
    const totalLng = hotelsWithCoords.reduce((sum, h) => {
      return sum + (h.Position?.Longitude || h.Longitude || 0);
    }, 0);
    
    return {
      lat: totalLat / hotelsWithCoords.length,
      lng: totalLng / hotelsWithCoords.length,
    };
  }, [searchResults]);

  // Get list of hotel IDs from search results
  const hotelIds = useMemo(() => {
    return searchResults.map(h => h.HotelId.toString());
  }, [searchResults]);

  // Fetch recommended hotels (those with manual info)
  const { recommendedHotelIds, hasRecommendedHotels } = useRecommendedHotels(hotelIds);

  const [filters, setFilters] = useState({
    priceRange: [0, 500] as [number, number],
    sortBy: "default",
    boardTypes: [] as string[],
    starRatings: [] as number[],
    cancellationPolicy: "all",
    hotelNameSearch: "",
    recommendedOnly: false,
  });

  const userAdjustedPriceRef = useRef(false);

  useEffect(() => {
    if (!hasSearched) return;
    if (!userAdjustedPriceRef.current) {
      const upper = Math.ceil(maxPricePerNight || 0);
      setFilters((prev) => ({
        ...prev,
        priceRange: [0, upper],
      }));
    }
  }, [maxPricePerNight, hasSearched]);

  useEffect(() => {
    if (hasAutoSearched.current) return;
    
    const destinationId = urlSearchParams.get('destinationId');
    const destinationNameParam = urlSearchParams.get('destinationName');
    const checkIn = urlSearchParams.get('checkIn');
    const checkOut = urlSearchParams.get('checkOut');
    const numNights = urlSearchParams.get('numNights');
    const adults = urlSearchParams.get('adults');
    const childrenAges = urlSearchParams.get('childrenAges');

    // Set destination display name from URL
    if (destinationNameParam) {
      setDestinationDisplayName(decodeURIComponent(destinationNameParam));
    }

    // Support both checkOut (from HeroSearchForm) and numNights (legacy)
    let finalCheckOut = checkOut;
    if (!finalCheckOut && numNights && checkIn) {
      const checkInDate = new Date(checkIn);
      const checkOutDate = new Date(checkInDate);
      checkOutDate.setDate(checkOutDate.getDate() + parseInt(numNights));
      finalCheckOut = checkOutDate.toISOString().split('T')[0];
    }

    if (destinationId && checkIn && finalCheckOut && adults) {
      hasAutoSearched.current = true;
      
      const params: HotelSearchParams = {
        destinationId,
        destinationName: destinationNameParam ? decodeURIComponent(destinationNameParam) : undefined,
        checkIn,
        checkOut: finalCheckOut,
        adults: parseInt(adults),
        childrenAges: childrenAges ? childrenAges.split(',').map(Number).filter(age => !isNaN(age)) : [],
      };
      
      handleSearch(params);
    }
  }, [urlSearchParams]);

  const handleFiltersChange = (next: typeof filters) => {
    if (
      next?.priceRange &&
      (next.priceRange[0] !== filters.priceRange[0] ||
        next.priceRange[1] !== filters.priceRange[1])
    ) {
      userAdjustedPriceRef.current = true;
    }

    // Avoid UI jank while filtering large result sets
    startTransition(() => {
      setFilters(next);
    });
  };

  const handleSearch = async (params: HotelSearchParams) => {
    setIsSearching(true);
    setHasSearched(true);
    setSearchParams(params);
    setCurrentPage(1);
    userAdjustedPriceRef.current = false;
    setSearchTimestamp(new Date());
    
    // Update destination display name when a new search is performed
    if (params.destinationName) {
      setDestinationDisplayName(params.destinationName);
    }
    
    try {
      const { data, error } = await supabase.functions.invoke('iterpec-search-hotels', {
        body: params
      });

      if (error) throw error;

      const response = data as IterpecSearchResponse;
      setSearchResults(response.Hotels || []);
      setSearchToken(response.Token || null);
      
      setTimeout(() => {
        if (resultsRef.current) {
          const yOffset = -80;
          const element = resultsRef.current;
          const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
      
      toast({
        title: t('hotels.searchComplete'),
        description: t('hotels.hotelsFound', { count: response.Hotels?.length || 0 }),
      });
    } catch (error) {
      console.error('Error searching hotels:', error);
      toast({
        title: t('errors.generic'),
        description: t('errors.searchFailed'),
        variant: "destructive",
      });
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const filteredAndSortedHotels = useMemo(() => {
    let filtered = searchResults;

    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;

    // Filter by recommended only
    if (filters.recommendedOnly) {
      filtered = filtered.filter(hotel => 
        recommendedHotelIds.has(hotel.HotelId.toString())
      );
    }

    // Filter by hotel name search
    if (filters.hotelNameSearch && filters.hotelNameSearch.trim()) {
      const searchTerm = filters.hotelNameSearch.toLowerCase().trim();
      filtered = filtered.filter(hotel => 
        hotel.Name.toLowerCase().includes(searchTerm)
      );
    }

    filtered = filtered.filter(hotel => {
      if (!hotel.Rooms || hotel.Rooms.length === 0) return true;
      const prices = hotel.Rooms.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0);
      if (prices.length === 0) return true;
      const minRoomPriceTotal = Math.min(...prices);
      const minRoomPricePerNight = totalNights > 0 ? minRoomPriceTotal / totalNights : minRoomPriceTotal;
      return minRoomPricePerNight >= filters.priceRange[0] && minRoomPricePerNight <= filters.priceRange[1];
    });

    if (filters.starRatings.length > 0) {
      filtered = filtered.filter(hotel => {
        const rating = (hotel as any).Category || hotel.StarRating;
        return filters.starRatings.includes(rating);
      });
    }

    if (filters.boardTypes.length > 0) {
      filtered = filtered.filter((hotel) => {
        if (!hotel.Rooms) return false;
        return hotel.Rooms.some((room) => {
          if (!room.BoardDescription) return false;
          const translatedBoard = translateBoardType(room.BoardDescription, locale);
          return filters.boardTypes.includes(translatedBoard);
        });
      });
    }

    if (filters.cancellationPolicy !== "all") {
      filtered = filtered.filter(hotel => {
        if (!hotel.Rooms) return false;
        
        return hotel.Rooms.some(room => {
          const hasPolicy = room.CancellationPolicies && room.CancellationPolicies.length > 0;
          if (!hasPolicy) return filters.cancellationPolicy === "refundable";
          
          const firstPolicy = room.CancellationPolicies![0];
          const parseDateOnly = (input: string) => {
            const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
              const [, y, m, d] = match;
              return new Date(Number(y), Number(m) - 1, Number(d));
            }
            const d = new Date(input);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          };

          const policyStartDate = parseDateOnly(firstPolicy.StartDate);
          const baseSearch = searchTimestamp ? new Date(searchTimestamp) : new Date();
          const searchDate = new Date(baseSearch.getFullYear(), baseSearch.getMonth(), baseSearch.getDate());
          const searchDatePlus5 = new Date(searchDate);
          searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);

          const isNonRefundable = policyStartDate <= searchDatePlus5;
          
          if (filters.cancellationPolicy === "non-refundable") {
            return isNonRefundable;
          } else {
            return !isNonRefundable;
          }
        });
      });
    }

    // Sort hotels - recommended first, then by selected sort
    filtered.sort((a, b) => {
      const aIsRecommended = recommendedHotelIds.has(a.HotelId.toString());
      const bIsRecommended = recommendedHotelIds.has(b.HotelId.toString());
      
      // Recommended hotels come first (unless filtering by recommendedOnly which means all are recommended)
      if (!filters.recommendedOnly && aIsRecommended !== bIsRecommended) {
        return aIsRecommended ? -1 : 1;
      }
      
      const aPrices = a.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const bPrices = b.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const aPrice = aPrices.length > 0 ? Math.min(...aPrices) : 0;
      const bPrice = bPrices.length > 0 ? Math.min(...bPrices) : 0;
      
      switch (filters.sortBy) {
        case "price_asc":
          return aPrice - bPrice;
        case "price_desc":
          return bPrice - aPrice;
        case "name_asc":
          return a.Name.localeCompare(b.Name);
        case "name_desc":
          return b.Name.localeCompare(a.Name);
        default:
          return 0;
      }
    });

    return filtered;
  }, [searchResults, filters, searchParams, searchTimestamp, locale, recommendedHotelIds]);

  const paginatedHotels = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return filteredAndSortedHotels.slice(startIndex, endIndex);
  }, [filteredAndSortedHotels, currentPage]);

  const totalPages = Math.ceil(filteredAndSortedHotels.length / ITEMS_PER_PAGE);

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    if (!hasSearched || currentPage === 1) return;
    
    setIsPageChanging(true);
    
    setTimeout(() => {
      if (resultsRef.current) {
        const yOffset = -80;
        const element = resultsRef.current;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
      
      setTimeout(() => setIsPageChanging(false), 50);
    }, 100);
  }, [currentPage, hasSearched]);

  const hotelListSchema = useMemo(() => {
    if (!paginatedHotels.length || !searchParams) return null;
    
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://www.foradarota.com';
    const hotelData = paginatedHotels.map(hotel => {
      const prices = hotel.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
      const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
      const totalNights = searchParams 
        ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
        : 1;
      const pricePerNight = totalNights > 0 ? minPrice / totalNights : minPrice;
      
      const detailUrl = `${baseUrl}/hoteles/${hotel.HotelId}?destinationId=${searchParams.destinationId}&checkIn=${searchParams.checkIn}&numNights=${totalNights}&adults=${searchParams.adults}${searchParams.childrenAges.length ? `&childrenAges=${searchParams.childrenAges.join(',')}` : ''}`;
      
      return {
        name: hotel.Name,
        url: detailUrl,
        image: hotel.ThumbUrl,
        starRating: (hotel as any).Category || hotel.StarRating,
        pricePerNight,
        address: hotel.Address
      };
    });
    
    return getHotelListSchema(hotelData, locale as LocaleKey);
  }, [paginatedHotels, searchParams, locale]);

  const baseUrl = getLocalizedUrl(locale as LocaleKey, '/');
  const breadcrumbSchema = getBreadcrumbSchema([
    { name: t('nav.home'), url: baseUrl },
    { name: t('hotels.breadcrumb'), url: getLocalizedUrl(locale as LocaleKey, '/hoteles') }
  ]);
  const organizationSchema = getOrganizationSchema(locale as LocaleKey);
  const webSiteSchema = getWebSiteSchema(locale as LocaleKey);

  const renderHotelCard = (hotel: IterpecHotel) => {
    const prices = hotel.Rooms?.map(r => r.PriceEur).filter((p): p is number => p !== undefined && p > 0) || [];
    const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
    const images = hotel.ThumbUrl ? [hotel.ThumbUrl] : [];
    
    const cheapestRoomIndex = hotel.Rooms?.findIndex(r => {
      const roomPrice = r.PriceEur;
      if (!roomPrice) return false;
      return roomPrice === minPrice;
    }) ?? -1;
    
    const cheapestRoom = cheapestRoomIndex >= 0 ? hotel.Rooms?.[cheapestRoomIndex] : null;
    
    const totalNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    const pricePerNight = totalNights > 0 ? minPrice / totalNights : minPrice;
    
    const cancellationInfo = cheapestRoom?.CancellationPolicies && cheapestRoom.CancellationPolicies.length > 0
      ? (() => {
          const firstPolicy = cheapestRoom.CancellationPolicies[0];

          const parseDateOnly = (input: string) => {
            const match = input.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
              const [, y, m, d] = match;
              return new Date(Number(y), Number(m) - 1, Number(d));
            }
            const d = new Date(input);
            return new Date(d.getFullYear(), d.getMonth(), d.getDate());
          };

          const policyStartDate = parseDateOnly(firstPolicy.StartDate);

          const baseSearch = searchTimestamp ? new Date(searchTimestamp) : new Date();
          const searchDate = new Date(baseSearch.getFullYear(), baseSearch.getMonth(), baseSearch.getDate());

          const checkInDateParsed = searchParams ? parseDateOnly(searchParams.checkIn) : null;
          const checkInDate = checkInDateParsed || searchDate;

          const searchDatePlus5 = new Date(searchDate);
          searchDatePlus5.setDate(searchDatePlus5.getDate() + 5);

          const checkInMinus1 = new Date(checkInDate);
          checkInMinus1.setDate(checkInMinus1.getDate() - 1);

          let text = '';
          let type: 'non-refundable' | 'refundable' | 'refundable-until' = 'refundable';

          const getLocaleForDate = () => {
            if (i18n.language === 'pt-BR') return 'pt-BR';
            if (i18n.language === 'en') return 'en-US';
            return 'es-ES';
          };

          if (policyStartDate <= searchDatePlus5) {
            text = t('hotels.nonRefundable');
            type = 'non-refundable';
          } else if (policyStartDate > searchDatePlus5 && policyStartDate < checkInMinus1) {
            const displayDate = new Date(policyStartDate);
            displayDate.setDate(displayDate.getDate() - 3);
            text = t('hotels.refundableUntil', { date: displayDate.toLocaleDateString(getLocaleForDate()) });
            type = 'refundable-until';
          } else {
            text = t('hotels.refundable');
            type = 'refundable';
          }

          return { text, type };
        })()
      : null;
    
    const numNights = searchParams 
      ? Math.ceil((new Date(searchParams.checkOut).getTime() - new Date(searchParams.checkIn).getTime()) / (1000 * 60 * 60 * 24))
      : 1;
    
    const params = new URLSearchParams({
      destinationId: searchParams?.destinationId || '',
      checkIn: searchParams?.checkIn || '',
      numNights: numNights.toString(),
      adults: searchParams?.adults?.toString() || '',
    });
    
    if (searchParams?.destinationName) {
      params.append('destinationName', searchParams.destinationName);
    }
    
    if (searchParams?.childrenAges && searchParams.childrenAges.length > 0) {
      params.append('childrenAges', searchParams.childrenAges.join(','));
    }
    
    const detailUrl = `/hoteles/${hotel.HotelId}?${params.toString()}`;
    
    const handleAddToCart = async () => {
      if (!cheapestRoom) {
        toast({
          title: t('common.error'),
          description: t('hotels.noRoomsAvailable'),
          variant: "destructive",
        });
        return;
      }

      try {
        const { data: productData, error: productError } = await supabase.functions.invoke(
          'save-hotel-product',
          {
            body: {
              hotelId: hotel.HotelId,
              roomId: cheapestRoom.Id.toString(),
              roomDescription: cheapestRoom.RoomDescription || 'Quarto',
              bedDescription: cheapestRoom.BedDescription || null,
              boardDescription: cheapestRoom.BoardDescription || null,
              priceEur: minPrice,
              hotelName: hotel.Name,
            },
          }
        );

        if (productError || !productData?.success) {
          throw new Error('Failed to save product');
        }

        const checkInDate = new Date(searchParams?.checkIn || '');
        const checkOutDate = new Date(searchParams?.checkOut || '');
        const cartNumNights = Math.ceil((checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24));

        const cancellationPolicy = cheapestRoom.CancellationPolicies?.[0];
        const cancellationType = cancellationPolicy ? 'Reembolsable' : 'No reembolsable';
        const cancellationText = cancellationPolicy 
          ? `Reembolsable hasta ${new Date(cancellationPolicy.StartDate).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}`
          : 'No reembolsable';

        await addItem({
          productId: productData.productId,
          bookingDetails: {
            type: 'hotel',
            hotelId: hotel.HotelId,
            hotelName: hotel.Name,
            hotelAddress: hotel.Address || '',
            roomId: cheapestRoom.Id.toString(),
            roomName: cheapestRoom.RoomDescription,
            roomDescription: cheapestRoom.RoomDescription,
            bedDescription: cheapestRoom.BedDescription || null,
            boardType: cheapestRoom.BoardDescription,
            boardDescription: cheapestRoom.BoardDescription,
            checkIn: searchParams?.checkIn,
            checkOut: searchParams?.checkOut,
            numNights: cartNumNights.toString(),
            adults: searchParams?.adults,
            childrenAges: searchParams?.childrenAges,
            priceEur: minPrice,
            propertyTaxesEur: cheapestRoom.PropertyTaxesEur,
            cancellationPolicies: cheapestRoom.CancellationPolicies,
            cancellationType,
            cancellationPolicy: cancellationText,
            cancellationStartDate: cancellationPolicy?.StartDate || null,
          },
          searchToken: searchToken || undefined,
        });
      } catch (error) {
        console.error('Error adding to cart:', error);
        toast({
          title: t('common.error'),
          description: t('cart.updateError'),
          variant: "destructive",
        });
      }
    };
    
    const isRecommended = recommendedHotelIds.has(hotel.HotelId.toString());
    
    return (
      <ProductCard 
        key={hotel.HotelId}
        detailsUrl={detailUrl}
        onAddToCart={handleAddToCart}
        isRecommended={isRecommended}
        product={{
          id: hotel.HotelId.toString(),
          name: hotel.Name,
          name_es: hotel.Name,
          description: hotel.Address,
          description_es: hotel.Address,
          product_type: "hotel",
          price_eur: minPrice,
          location: hotel.Address,
          images: images,
          metadata: {
            starRating: (hotel as any).Category || hotel.StarRating,
            rooms: hotel.Rooms,
            position: hotel.Position,
            roomDescription: cheapestRoom?.RoomDescription,
            boardDescription: cheapestRoom?.BoardDescription,
            cancellationInfo,
            pricePerNight,
            totalNights,
            propertyTaxesEur: cheapestRoom?.PropertyTaxesEur,
          },
          provider: "iterpec",
          is_active: true,
        }} 
      />
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <HreflangTags path="/hoteles" />
<SchemaOrg schema={hotelListSchema ? [organizationSchema, webSiteSchema, breadcrumbSchema, hotelListSchema] : [organizationSchema, webSiteSchema, breadcrumbSchema]} />
      <Navbar />
      
      {/* Hero Section */}
      <section className="bg-primary/5 border-b border-border">
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3">
              {t('hotels.title')}
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('hotels.subtitle')}
            </p>
          </div>
          <HotelSearchForm onSearch={handleSearch} loading={isSearching} />
        </div>
      </section>

      <main id="main-content" className="container mx-auto px-4 py-8" role="main">
        {/* Dynamic Breadcrumbs */}
        <Breadcrumb 
          items={
            destinationDisplayName 
              ? [
                  { label: t('hotels.breadcrumb'), href: '/hoteles' },
                  { label: destinationDisplayName }
                ]
              : [{ label: t('hotels.breadcrumb') }]
          } 
        />
        {!hasSearched ? (
          /* Empty State */
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
              <Search className="h-10 w-10 text-primary" />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-3">
              {t('hotels.emptyStateTitle', 'Comece sua busca')}
            </h2>
            <p className="text-muted-foreground max-w-md mx-auto">
              {t('hotels.emptyStateDescription', 'Escolha um destino e as datas da sua viagem para encontrar os melhores hotéis.')}
            </p>
          </div>
        ) : (
          <div ref={resultsRef}>
            {/* Destination Title */}
            {destinationDisplayName && (
              <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                {t('hotels.hotelsIn', { destination: destinationDisplayName }) || `Hoteles en ${destinationDisplayName}`}
              </h2>
            )}
            
            {/* Results Header - Mobile optimized */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
              {/* Results count - only show when not searching */}
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-primary shrink-0" />
                <span className="text-sm sm:text-base font-medium text-foreground">
                  {isSearching ? (
                    t('hotels.searching')
                  ) : (
                    `${filteredAndSortedHotels.length} ${t('hotels.propertiesFound', 'hotéis encontrados')}`
                  )}
                </span>
              </div>
              
              {/* Action buttons - Compact on mobile */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Mobile Filter Button */}
                {searchResults.length > 0 && (
                  <div className="lg:hidden">
                    <HotelFiltersSidebar
                      filters={filters}
                      onFiltersChange={handleFiltersChange}
                      maxPrice={maxPricePerNight}
                      boardTypes={boardTypes}
                      starRatings={starRatings}
                      resultsCount={filteredAndSortedHotels.length}
                      destinationCoordinates={destinationCoordinates}
                      onOpenMap={() => setIsMapOpen(true)}
                      hasRecommendedHotels={hasRecommendedHotels}
                    />
                  </div>
                )}
                
                {/* Map button */}
                {searchResults.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsMapOpen(true)}
                    className="gap-2"
                    aria-label={t('hotels.viewMap')}
                  >
                    <Map className="h-4 w-4" />
                    <span className="hidden sm:inline">{t('hotels.viewMap')}</span>
                  </Button>
                )}
                
                {/* Sort dropdown */}
                <Select
                  value={filters.sortBy}
                  onValueChange={(value) => handleFiltersChange({ ...filters, sortBy: value })}
                >
                  <SelectTrigger className="w-[130px] sm:w-[160px] h-9 text-sm">
                    <SelectValue placeholder={t('filters.sortBy')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="default">{t('filters.default')}</SelectItem>
                    <SelectItem value="price_asc">{t('filters.priceLowToHigh')}</SelectItem>
                    <SelectItem value="price_desc">{t('filters.priceHighToLow')}</SelectItem>
                    <SelectItem value="name_asc">{t('filters.nameAZ')}</SelectItem>
                    <SelectItem value="name_desc">{t('filters.nameZA')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Main Content with Sidebar */}
            <div className="flex gap-8">
              {/* Desktop Filters Sidebar */}
              {searchResults.length > 0 && (
                <div className="hidden lg:block">
                  <HotelFiltersSidebar
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    maxPrice={maxPricePerNight}
                    boardTypes={boardTypes}
                    starRatings={starRatings}
                    resultsCount={filteredAndSortedHotels.length}
                    destinationCoordinates={destinationCoordinates}
                    onOpenMap={() => setIsMapOpen(true)}
                    hasRecommendedHotels={hasRecommendedHotels}
                  />
                </div>
              )}

              {/* Results Grid */}
              <div className="flex-1 min-w-0">
                {isSearching ? (
                  <div className="space-y-8">
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                      <p className="mt-4 text-lg font-medium text-foreground">{t('hotels.searching')}</p>
                      <p className="mt-2 text-sm text-muted-foreground">{t('hotels.searchingSubtitle')}</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3">
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="space-y-3">
                          <Skeleton className="h-40 sm:h-48 w-full rounded-lg" />
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <Skeleton className="h-4 w-full" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : filteredAndSortedHotels.length === 0 ? (
                  <div className="rounded-lg border border-border bg-card p-8 sm:p-12 text-center">
                    <p className="text-base sm:text-lg text-muted-foreground">
                      {t('hotels.noResults')}
                    </p>
                  </div>
                ) : (
                  <>
                    <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 ${!isPageChanging ? 'animate-fade-in' : 'opacity-0'}`}>
                      {paginatedHotels.map(renderHotelCard)}
                    </div>
                    
                    {totalPages > 1 && (
                      <div className="mt-8 flex justify-center">
                        <Pagination>
                          <PaginationContent>
                            <PaginationItem>
                              <PaginationPrevious 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage > 1) {
                                    setCurrentPage(currentPage - 1);
                                  }
                                }}
                                className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                            
                            {[...Array(totalPages)].map((_, i) => {
                              const pageNum = i + 1;
                              if (
                                pageNum === 1 ||
                                pageNum === totalPages ||
                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                              ) {
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationLink
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        setCurrentPage(pageNum);
                                      }}
                                      isActive={currentPage === pageNum}
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              } else if (
                                pageNum === currentPage - 2 ||
                                pageNum === currentPage + 2
                              ) {
                                return (
                                  <PaginationItem key={pageNum}>
                                    <PaginationEllipsis />
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}
                            
                            <PaginationItem>
                              <PaginationNext 
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (currentPage < totalPages) {
                                    setCurrentPage(currentPage + 1);
                                  }
                                }}
                                className={currentPage === totalPages ? 'pointer-events-none opacity-50' : ''}
                              />
                            </PaginationItem>
                          </PaginationContent>
                        </Pagination>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
      
      <Footer />

      {/* Hotels Map Modal - Lazy loaded */}
      <LazyHotelsMapModal
        isOpen={isMapOpen}
        onClose={() => setIsMapOpen(false)}
        hotels={filteredAndSortedHotels}
      />
    </div>
  );
};

export default Hotels;